import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { getShortLanguageCode } from 'utils/format';
import { googleStaticMapUrl } from 'utils/google-static-map';
import SupplierServiceChips from './supplier-service-chips.component';
import SupplierProfileImage from './supplier-profile-image.component';
import DirectoryChainNote from './directory-chain-note.component';
import { DirectoryBrowserConfig, DirectoryLab } from './directory-browser-config';

type SupplierDetailsDialogProps = {
    lab?: DirectoryLab;
    onClose: () => void;
    config: DirectoryBrowserConfig;
};

const withProtocol = (website: string) => (/^https?:\/\//i.test(website) ? website : `https://${website}`);

/** DD-3674: images and the map stay short so the description and contact stay in view. */
const IMAGE_MAX_HEIGHT = 160;
/** A wide logo would otherwise push the second image off to a sliver. */
const LOGO_MAX_WIDTH = 340;
const MAP_HEIGHT = 120;
const SECOND_IMAGE_MAX_WIDTH = 300;

/**
 * DD-3662: a listed lab's whole public profile — description, services, contact details.
 * DD-3674: logo left and second image right, a static map beside the contact details.
 */
const SupplierDetailsDialog: FC<SupplierDetailsDialogProps> = ({ lab, onClose, config }) => {
    const { t: translate } = useTranslation(['client-directory', 'common', 'organization']);
    const address = [lab?.street, [lab?.zip, lab?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const mapUrl = googleStaticMapUrl(
        lab?.geoLocation,
        getShortLanguageCode(currentUserVar()?.localeFormat?.language),
        `290x${MAP_HEIGHT}`,
    );

    return (
        <DHDialog
            open={Boolean(lab)}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            title={lab?.name}
            actions={lab ? config.renderAction(lab, onClose) : undefined}
        >
            {lab?.logoSignedUrl || lab?.staffSignedUrl ? (
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
                    {/* Each side keeps its place, so the second image stays right without a logo. */}
                    <Box minWidth={0}>
                        <SupplierProfileImage
                            url={lab.logoSignedUrl}
                            alt={lab.name ?? ''}
                            maxHeight={IMAGE_MAX_HEIGHT}
                            maxWidth={LOGO_MAX_WIDTH}
                            dataTest="supplier-details.logo"
                        />
                    </Box>
                    <Box flexShrink={0}>
                        <SupplierProfileImage
                            url={lab.staffSignedUrl}
                            alt=""
                            maxHeight={IMAGE_MAX_HEIGHT}
                            maxWidth={SECOND_IMAGE_MAX_WIDTH}
                            dataTest="supplier-details.image"
                        />
                    </Box>
                </Box>
            ) : undefined}
            {/* DD-3698: the type chips left, the website (when there is one) right. */}
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <SupplierServiceChips services={lab?.services ?? []} labelKey={config.serviceLabelKey} />
                {lab?.website ? (
                    <Button
                        href={withProtocol(lab.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        sx={{ flexShrink: 0, ml: 'auto' }}
                        data-test="supplier-details.website"
                    >
                        {translate('client-directory:OPEN_WEBSITE')}
                    </Button>
                ) : undefined}
            </Box>
            {lab ? (
                <Box mt={1}>
                    <DirectoryChainNote lab={lab} variant="subtitle1" />
                </Box>
            ) : undefined}
            {lab?.description || config.texts.noDescription ? (
                <Typography
                    variant="body1"
                    mt={2}
                    sx={{ whiteSpace: 'pre-line' }}
                    color={lab?.description ? 'text.primary' : 'text.secondary'}
                >
                    {lab?.description || translate(config.texts.noDescription ?? '')}
                </Typography>
            ) : undefined}
            {lab && config.renderDetails ? config.renderDetails(lab) : undefined}

            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={mapUrl ? 6 : 12}>
                    <Typography variant="subtitle2" mb={0.5}>
                        {translate('client-directory:SUPPLIER_CONTACT')}
                    </Typography>
                    {lab?.organizationNumber ? (
                        <Typography variant="body2">
                            {translate('organization:ORGANIZATION_NUMBER')}: {lab.organizationNumber}
                        </Typography>
                    ) : undefined}
                    {address ? <Typography variant="body2">{address}</Typography> : undefined}
                    {lab?.phone ? (
                        <Typography variant="body2">
                            {translate('common:PHONE')}: {lab.phone}
                        </Typography>
                    ) : undefined}
                    {lab?.email ? (
                        <Typography variant="body2">
                            {translate('common:EMAIL')}: <Link href={`mailto:${lab.email}`}>{lab.email}</Link>
                        </Typography>
                    ) : undefined}
                    {lab?.website ? (
                        <Typography variant="body2">
                            {translate('common:WEBSITE')}:{' '}
                            <Link href={withProtocol(lab.website)} target="_blank" rel="noopener noreferrer">
                                {lab.website}
                            </Link>
                        </Typography>
                    ) : undefined}
                </Grid>
                {mapUrl ? (
                    <Grid item xs={12} sm={6} display="flex" justifyContent="flex-end">
                        <Box
                            component="img"
                            src={mapUrl}
                            alt="Google Map"
                            sx={{ display: 'block', maxWidth: '100%', borderRadius: 1 }}
                            data-test="supplier-details.map"
                        />
                    </Grid>
                ) : undefined}
            </Grid>
        </DHDialog>
    );
};

export default SupplierDetailsDialog;
